// @flow
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import moment from 'moment';

import DatePicker from 'react-datepicker/es';
import DialogActions from '@material-ui/core/DialogActions/DialogActions';
import DialogContent from '@material-ui/core/DialogContent/DialogContent';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core';

import { Field, Form, Formik } from 'formik';
import type { FormikBag } from 'formik';
import * as Yup from 'yup';

import AccountTypeSelector from 'components/Formik/AccountTypeSelector';
import AmountField from 'components/QuickenControls/AmountField';
import FormikTextField from 'components/Formik/TextField';
import QButton from 'components/QButton';

import { upsertAccounts } from 'data/accounts/actions';
import { mkAccount } from 'data/accounts/types';
import { getAccountsById } from 'data/accounts/selectors';
import { createOpeningBalanceTransactionAction } from 'data/transactions/actions';
import * as txnTypes from 'data/transactions/types';

import tracker from 'utils/tracker';
import { isAcme } from 'isAcme';

import * as selectors from '../selectors';

const useStyles = makeStyles((theme) => ({
  dialogContentRoot: {
    marginTop: 20,
    flex: '1 1 auto',
  },
  accountName: {
    marginTop: 12,
    display: 'flex',
    width: 552,
    height: 82,
  },
  accountTypeFormControl: {
    marginTop: 16,
    display: 'flex',
    width: 552,
    height: 82,
  },
  form: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  formActions: {
    margin: 16,
    justifyContent: 'flex-end',
    marginLeft: 24,
  },
  inputLabel: {
    backgroundColor: theme.palette.greyScale[7],
    paddingLeft: 2,
    paddingRight: 3,
  },
  manualLine: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: theme.palette.greyScale[2],
  },
  rightSide: {
    display: 'inline-block',
  },
  statementFields: {
    width: 252,
  },
  statementRow: {
    display: 'flex',
    width: 552,
    marginTop: 16,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
}));

const schema = Yup.object().shape({
  accountName: Yup.string()
    .min(2, 'Must be greater than 2 characters')
    .max(64, 'Must be less than 64 characters')
    .required('Required'),
  accountType: Yup.string()
    .required('Required'),
});

type Props = {
  trackingProperties: Object,
  onCancel: () => void,
};

const NonConnectedAccount = (props: Props) => {
  const { onCancel, trackingProperties } = props;

  const dispatch = useDispatch();

  const accountsById = useSelector(getAccountsById);
  const isSubmitting = useSelector(selectors.getIsSubmitting);

  const classes = useStyles();

  const validateOriginalName = (name) => {
    let ret = null;
    const lowerCaseName = name.toLowerCase();
    accountsById.forEach((account) => {
      if (account.name.toLowerCase() === lowerCaseName) {
        ret = 'Name must be unique. Pick a name different from other accounts';
      }
    });
    return ret;
  };

  const handleSubmit = (values, _actions) => {

    const accountParams = {
      clientId: uuidv4().toUpperCase(),

      name: values.accountName,
      subType: values.accountType.qcsSubType,
      type: values.accountType.qcsType,

      balanceAsOf: 0.0,
      balanceAsOfOn: '1970-01-01',
    };

    const accountToUpsert = mkAccount(values.accountType.qcsType, accountParams);

    const openingBalanceAsOf = values.statementBalance && Number(parseFloat(values.statementBalance).toFixed(2)) || 0.0;
    const openingBalanceAsOfOn = values.statementDate && moment(values.statementDate).format('YYYY-MM-DD') || '1970-01-01';
    let metadata = null;

    if (!isAcme || openingBalanceAsOf !== 0.0) {
      const amountSign = values.accountType && ['CREDIT', 'OTHER_LIABILITY'].includes(values.accountType.qcsType) ? -1 : 1;
      const newTxn = new txnTypes.CashFlowTransaction({
        payee: 'Opening Balance',
        coa: {
          type: 'BALANCE_ADJUSTMENT',
          id: '0',
        },
        state: 'RECONCILED',
        isReviewed: true,
        amount: openingBalanceAsOf * amountSign,
        postedOn: openingBalanceAsOfOn,
        clientId: uuidv4().toUpperCase(),
        isExcludedFromReports: true,
        isExcludedFromF2S: true,
      });
      metadata = {
        afterSuccessAction: createOpeningBalanceTransactionAction(
          {
            newTxn,
            accountClientId: accountToUpsert.clientId,
          },
          { context: 'addAccounts' },
        ),
      };
    }

    dispatch(upsertAccounts([accountToUpsert], metadata));
    tracker.track(tracker.events.addFIComplete, ({
      ...trackingProperties,
      accounts_added: 1,
      accounts_ignored: 0,
      accounts_linked: 0,
      accounts_changed_name: 0,
      accounts_changed_type: 0,
      accounts_changed_subtype: 0,
      accounts_already_connected: 0,
    }));
  };

  const renderForm = (formikBag: FormikBag) => {
    const {
      handleSubmit: formikHandleSubmit,
      errors,
      touched,
      isValid,
      values,
    } = formikBag;

    let statementDateLabel = 'Statement Ending Date';
    let statementBalanceLabel = 'Statement Ending Balance';
    let showDate = true;

    const { accountType } = values;
    if (accountType) {
      if (isAcme) {
        statementBalanceLabel = 'Balance';
        statementDateLabel = 'Date';
        // hide date and update label for asset accounts
        if (accountType.id === 'OTHER_ASSET' || accountType.id === 'REAL_ESTATE' || accountType.id === 'VEHICLE') {
          showDate = false;
          statementBalanceLabel = 'Value';
        }
      } else {
        if (accountType.id === 'LIABILITY') {
          statementBalanceLabel = 'Liability Amount';
          statementDateLabel = 'Date to start tracking';
        }
        if (accountType.id === 'OTHER_ASSET') {
          statementBalanceLabel = 'Asset value';
          statementDateLabel = 'Date to start tracking';
        }
      }
    }

    return (
      <Form className={classes.form} onSubmit={formikHandleSubmit}>
        <DialogContent classes={{ root: classes.dialogContentRoot }} style={{ padding: !isAcme && '12px 24px' }}>
          <Field
            component={FormikTextField}
            name="accountName"
            label="Account Name"
            variant="outlined"
            className={classes.accountName}
            inputProps={{
              id: "manual_account_name",
            }}
            validate={validateOriginalName}
          />

          <FormControl
            className={classes.accountTypeFormControl}
            variant="outlined"
            error={touched.accountType && Boolean(errors.accountType)}
          >
            <InputLabel htmlFor="manual_account_type" className={classes.inputLabel}>
              Account Type
            </InputLabel>
            <AccountTypeSelector
              fullWidth
              id="manual_account_type"
              name="accountType"
              showManualTypes
            />
            <FormHelperText>
              {touched.accountType ? errors.accountType : null}
            </FormHelperText>
          </FormControl>

          {values.accountType && (!isAcme || values.accountType.qcsType !== "INVESTMENT") &&
            <div className={classes.statementRow}>
              <Field
                name="statementBalance"
                validate={(value) => {
                  let errorMessage;
                  if (!value || !value.length) {
                    errorMessage = 'required';
                  }
                  return errorMessage;
                }}
                render={({ field }) => (
                  <AmountField
                    {...field}
                    variant="outlined"
                    showInitialSign={false}
                    hideCurrencyString
                    style={{ width: isAcme ? 252 : 268 }}
                    editable
                    omitCents
                    currencySymbol="USD"
                    amountType="amount"
                    id="schedtxn-amount"
                    label={statementBalanceLabel}
                    autoComplete="off"
                    error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                    helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                    InputProps={{ id: "manual_ending_balance" }}
                  />
                )}
              />

              {isAcme && showDate &&
                <Typography style={{ marginTop: 16 }}>
                  On
                </Typography>
              }

              {showDate &&
                <Field
                  name="statementDate"
                  validate={(value) => {
                    let errorMessage;
                    if (!value || !value.length) {
                      errorMessage = 'required';
                    } else if (!moment(value).isValid()) {
                      errorMessage = 'invalid';
                    }
                    return errorMessage;
                  }}
                  render={({ field }) => {
                    const date = moment(field.value);
                    const selected = date.isValid() ? date : moment();
                    return (
                      <DatePicker
                        id="manual_ending_date"
                        selected={selected}
                        onChange={(momentDate) => formikBag.setFieldValue('statementDate', moment(momentDate).format('l'))}
                        maxDate={moment().utc()}
                        allowSameDay
                        popperPlacement="top-end"
                        customInput={
                          <TextField
                            InputProps={{
                              ...field,
                              classes: {},
                              autoComplete: 'off',
                              style: {
                                width: isAcme ? 252 : 268,
                              },
                            }}
                            label={statementDateLabel}
                            variant="outlined"
                            error={Boolean(errors.statementDate && touched.statementDate)}
                            helperText={touched.statementDate && errors.statementDate}
                          />
                        }
                      />
                    );
                  }}
                />
              }
            </div>
          }
        </DialogContent>
        <DialogActions className={classes.formActions}>
          <div className={classes.rightSide}>
            <QButton
              variant="outlined"
              onClick={() => onCancel()}
              style={{ marginRight: 8 }}
              width={120}
              id="manual_back_btn"
            >
              Go Back
            </QButton>
            <QButton
              type="submit"
              variant="contained"
              disabled={!isValid || isSubmitting}
              width={120}
              id="manual_done_btn"
            >
              Continue
            </QButton>
          </div>
        </DialogActions>
      </Form>
    );
  };

  const initialVals = { statementDate: moment().format('l'), statementBalance: '0' };
  return (
    <Formik
      initialValues={initialVals}
      validationSchema={schema}
      onSubmit={(values, actions) => handleSubmit(values, actions)}
      render={renderForm}
    />
  );
};

export default NonConnectedAccount;
